import { Outlet, Link, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { formatRole } from '@/utils/formatRole'
import { TinxliLogo } from './TinxliLogo'
import {
  LayoutDashboard,
  Settings,
  Crown,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  Home,
  Wallet,
  Plug,
  CalendarDays,
  BarChart3,
  Package,
  Banknote,
  Rss,
  FileText,
  Code,
  HelpCircle,
  KeyRound,
  Tags,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// NavItem Component for sidebar links
function NavItem({
  to,
  label,
  icon: Icon,
  isActive,
  isCollapsed
}: {
  to: string
  label: string
  icon: LucideIcon
  isActive: boolean
  isCollapsed: boolean
}) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <div
      style={{
        padding: '0.25rem 0',
        position: 'relative'
      }}
      onMouseEnter={() => isCollapsed && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <Link
        to={to}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: isCollapsed ? '0.75rem' : '0.625rem 1rem',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          color: isActive ? '#ffffff' : '#a0aec0',
          textDecoration: 'none',
          background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
          borderRadius: 'var(--radius-md)',
          transition: `all var(--default-transition-duration) var(--ease-in-out)`,
          gap: '0.75rem',
          fontSize: 'var(--text-sm)',
          fontWeight: isActive ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)'
        }}
        onMouseEnter={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'
            e.currentTarget.style.color = '#ffffff'
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive) {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = '#a0aec0'
          }
        }}
      >
        <Icon
          size={18}
          style={{
            flexShrink: 0
          }}
        />
        {!isCollapsed && (
          <span style={{
            opacity: isCollapsed ? 0 : 1,
            transition: 'opacity 0.2s',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}>
            {label}
          </span>
        )}
      </Link>

      {/* Tooltip for collapsed state */}
      {isCollapsed && showTooltip && (
        <div style={{
          position: 'absolute',
          left: '90px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'var(--color-gray-800)',
          color: 'var(--color-white)',
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-sm)',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: `1px solid var(--color-gray-700)`,
          pointerEvents: 'none',
          animation: `fadeIn var(--default-transition-duration) var(--ease-in-out)`
        }}>
          {label}
        </div>
      )}
    </div>
  )
}

// Disabled NavItem for addon features not yet available
function DisabledNavItem({
  label,
  icon: Icon,
  isCollapsed
}: {
  label: string
  icon: LucideIcon
  isCollapsed: boolean
}) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <div
      style={{
        padding: '0.25rem 0',
        position: 'relative',
        cursor: 'default',
      }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: isCollapsed ? '0.75rem' : '0.625rem 1rem',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          color: '#4a5568',
          opacity: 0.5,
          borderRadius: 'var(--radius-md)',
          gap: '0.75rem',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-normal)',
        }}
      >
        <Icon size={18} style={{ flexShrink: 0 }} />
        {!isCollapsed && (
          <>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {label}
            </span>
            <span style={{
              marginLeft: 'auto',
              fontSize: '0.6rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              background: 'rgba(255, 255, 255, 0.1)',
              color: '#a0aec0',
              padding: '0.15rem 0.4rem',
              borderRadius: '3px',
              whiteSpace: 'nowrap',
            }}>
              Addon
            </span>
          </>
        )}
      </div>

      {/* Tooltip for collapsed state */}
      {isCollapsed && showTooltip && (
        <div style={{
          position: 'absolute',
          left: '90px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'var(--color-gray-800)',
          color: 'var(--color-white)',
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-sm)',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--color-gray-700)',
          pointerEvents: 'none',
          animation: 'fadeIn var(--default-transition-duration) var(--ease-in-out)',
        }}>
          {label} (Addon)
        </div>
      )}
    </div>
  )
}

// Section Header Component
function SectionHeader({
  label,
  isCollapsed
}: {
  label: string
  isCollapsed: boolean
}) {
  if (isCollapsed) return null

  return (
    <div style={{
      padding: '1rem 1rem 0.5rem 1rem',
      fontSize: 'var(--text-xs)',
      fontWeight: 'var(--font-weight-semibold)',
      color: '#718096',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }}>
      {label}
    </div>
  )
}

export default function Layout() {
  const { isAuthenticated, user, logout, impersonation, stopImpersonation } = useAuthStore()
  const location = useLocation()

  // Sidebar collapsed state with localStorage persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : false
  })

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)


  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed))
  }, [isCollapsed])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
      if (!mobile) {
        setIsMobileMenuOpen(false)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isActive = (path: string) => location.pathname === path
  const toggleSidebar = () => setIsCollapsed(!isCollapsed)
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)

  return (
    <div style={{ minHeight: '100vh', display: 'flex', position: 'relative' }}>
      {/* Mobile Menu Overlay */}
      {isAuthenticated && isMobileMenuOpen && isMobile && (
        <div
          onClick={toggleMobileMenu}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998
          }}
        />
      )}

      {/* Sidebar */}
      {isAuthenticated && (
        <aside style={{
          background: '#1a1d29',
          color: 'var(--color-white)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          position: isMobile ? 'fixed' : 'sticky',
          top: 0,
          left: isMobile ? (isMobileMenuOpen ? 0 : '-250px') : 0,
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: isMobile
            ? 'left var(--default-transition-duration) var(--ease-in-out)'
            : 'width var(--default-transition-duration) var(--ease-in-out)',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
          zIndex: 999,
          width: isMobile ? '250px' : (isCollapsed ? '80px' : '250px')
        }}>
          {/* Account/Environment Switcher */}
          <div style={{
            padding: '1rem',
            borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
            cursor: 'pointer',
            transition: 'background var(--default-transition-duration) var(--ease-in-out)'
          }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            {!isCollapsed ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#2d3142',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-white)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-bold)',
                  flexShrink: 0
                }}>
                  T
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--color-white)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {(user as any)?.vendorName || user?.name || 'Tixxly Inc'} sandbox
                  </div>
                  <div style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-gray-400)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {(user as any)?.vendorName || user?.name || 'Tixxly Inc'}
                  </div>
                </div>
                <ChevronDown size={16} style={{ color: '#a0aec0', flexShrink: 0 }} />
              </div>
            ) : (
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                background: '#2d3142',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-white)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-bold)',
                margin: '0 auto'
              }}>
                T
              </div>
            )}
          </div>

          {/* Collapse Toggle Button */}
          <div style={{
            padding: '0.5rem 1rem',
            borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={toggleSidebar}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-gray-400)',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all var(--default-transition-duration) var(--ease-in-out)',
                minWidth: '32px',
                minHeight: '32px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)'
                e.currentTarget.style.color = 'var(--color-white)'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = 'var(--color-gray-400)'
              }}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>

          {/* Navigation Links */}
          <nav style={{ flex: 1, padding: '0.75rem 0', overflowY: 'auto' }}>
            {(() => {
              const role = user?.role?.toUpperCase()
              const isVendor = role === 'VENDOR' || role === 'VENDOR_ADMIN' || impersonation.active

              if (isVendor) {
                return (
                  <>
                    <div style={{ padding: '0.5rem 0' }}>
                      <NavItem
                        to="/vendor"
                        label="Home"
                        icon={Home}
                        isActive={isActive('/vendor')}
                        isCollapsed={isCollapsed}
                      />
                    </div>

                    <SectionHeader label="Products" isCollapsed={isCollapsed} />
                    <div style={{ padding: '0.5rem 0' }}>
                      <NavItem
                        to="/vendor/inventory/experiences"
                        label="Experiences"
                        icon={Package}
                        isActive={isActive('/vendor/inventory/experiences')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/inventory"
                        label="Feeds & Syncs"
                        icon={Rss}
                        isActive={isActive('/vendor/inventory')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/integrations"
                        label="Integrations"
                        icon={Plug}
                        isActive={isActive('/vendor/integrations')}
                        isCollapsed={isCollapsed}
                      />
                    </div>

                    <SectionHeader label="Bookings & Payments" isCollapsed={isCollapsed} />
                    <div style={{ padding: '0.5rem 0' }}>
                      <NavItem
                        to="/vendor/bookings"
                        label="Bookings"
                        icon={CalendarDays}
                        isActive={isActive('/vendor/bookings')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/payments/link-account"
                        label="Payout Setup"
                        icon={Banknote}
                        isActive={isActive('/vendor/payments/link-account')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/subscriptions/manage"
                        label="Subscriptions"
                        icon={Crown}
                        isActive={isActive('/vendor/subscriptions/manage')}
                        isCollapsed={isCollapsed}
                      />
                    </div>

                    <SectionHeader label="AI & Distribution" isCollapsed={isCollapsed} />
                    <div style={{ padding: '0.5rem 0' }}>
                      <NavItem
                        to="/vendor/analytics"
                        label="Analytics"
                        icon={BarChart3}
                        isActive={isActive('/vendor/analytics')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/ai-connections"
                        label="AI Connections"
                        icon={Plug}
                        isActive={isActive('/vendor/ai-connections')}
                        isCollapsed={isCollapsed}
                      />
                      <DisabledNavItem label="Website Widget" icon={Code} isCollapsed={isCollapsed} />
                    </div>

                    <div style={{ marginTop: 'auto', padding: '0.5rem 0' }}>
                      <NavItem
                        to="/vendor/help"
                        label="Help"
                        icon={HelpCircle}
                        isActive={isActive('/vendor/help')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/supplier-agreement"
                        label="Agreement"
                        icon={FileText}
                        isActive={isActive('/vendor/supplier-agreement')}
                        isCollapsed={isCollapsed}
                      />
                      <NavItem
                        to="/vendor/settings"
                        label="Settings"
                        icon={Settings}
                        isActive={isActive('/vendor/settings')}
                        isCollapsed={isCollapsed}
                      />
                    </div>
                  </>
                )
              }

              return (
                <div style={{ padding: '0.5rem 0' }}>
                  <NavItem
                    to="/dashboard"
                    label="Dashboard"
                    icon={LayoutDashboard}
                    isActive={isActive('/dashboard')}
                    isCollapsed={isCollapsed}
                  />
                </div>
              )
            })()}

            {['SUPER_ADMIN', 'ADMIN', 'MODERATOR', 'VIEWER'].includes(user?.role?.toUpperCase() || '') && (
              <div style={{ padding: '0.5rem 0' }}>
                {!isCollapsed && <div style={{ padding: '0.5rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)' }}>Admin</div>}
                <NavItem to="/admin" label="Overview" icon={Crown} isActive={isActive('/admin') && !location.pathname.startsWith('/admin/')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/vendors" label="Vendors" icon={Home} isActive={location.pathname.startsWith('/admin/vendors')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/users" label="Team" icon={Settings} isActive={location.pathname.startsWith('/admin/users')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/pricing-tiers" label="Pricing Tiers" icon={Tags} isActive={location.pathname.startsWith('/admin/pricing-tiers')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/features" label="Features" icon={Wallet} isActive={location.pathname.startsWith('/admin/features')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/fees" label="Fee Plans" icon={Wallet} isActive={location.pathname.startsWith('/admin/fees')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/providers" label="Providers" icon={Wallet} isActive={location.pathname.startsWith('/admin/providers')} isCollapsed={isCollapsed} />
                <NavItem to="/admin/ai-connections" label="AI Connections" icon={Plug} isActive={location.pathname.startsWith('/admin/ai-connections')} isCollapsed={isCollapsed} />
                {['SUPER_ADMIN', 'ADMIN'].includes(user?.role?.toUpperCase() || '') && (
                  <NavItem to="/admin/api-keys" label="API Keys" icon={KeyRound} isActive={location.pathname.startsWith('/admin/api-keys')} isCollapsed={isCollapsed} />
                )}
              </div>
            )}
          </nav>

          {/* User Info & Logout */}
          <div style={{
            padding: isCollapsed ? '1rem 0.5rem' : '1.5rem',
            borderTop: `1px solid rgba(255, 255, 255, 0.1)`,
            background: '#151720'
          }}>
            {!isCollapsed && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)', marginBottom: '0.25rem' }}>
                  Hey,
                </p>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name || user?.email}
                </p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', marginTop: '0.25rem' }}>
                  {formatRole(user?.role)}
                </p>
              </div>
            )}
            {isCollapsed && (
              <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'var(--color-gray-700)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  fontSize: 'var(--text-xl)'
                }}>
                  {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
                </div>
              </div>
            )}
            <button
              onClick={logout}
              style={{
                width: '100%',
                padding: isCollapsed ? '0.75rem' : '0.75rem',
                background: 'var(--color-red-600)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                fontWeight: 'var(--font-weight-medium)',
                transition: `background var(--default-transition-duration) var(--ease-in-out)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isCollapsed ? '0' : '0.5rem',
                fontSize: 'var(--text-sm)'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = 'var(--color-red-700)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'var(--color-red-600)'}
              title={isCollapsed ? 'Logout' : undefined}
            >
              <LogOut size={isCollapsed ? 20 : 18} />
              {!isCollapsed && 'Logout'}
            </button>
          </div>
        </aside>
      )}

      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
        {/* Mobile Top Bar with Menu Toggle */}
        {isAuthenticated && isMobile && (
          <nav style={{
            background: 'var(--color-tixxly-navy)',
            color: 'var(--color-pure-white)',
            padding: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            borderBottom: `1px solid var(--color-gray-700)`,
            position: 'sticky',
            top: 0,
            zIndex: 997
          }}>
            <button
              onClick={toggleMobileMenu}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-white)',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: 'var(--radius-md)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Menu size={24} />
            </button>
            <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <TinxliLogo width={100} height={32} />
            </Link>
            <div style={{ width: '40px' }} /> {/* Spacer for centering */}
          </nav>
        )}

        {/* Top Bar (for non-authenticated users or additional info) */}
        {!isAuthenticated && (
          <nav style={{
            background: 'var(--color-tixxly-navy)',
            color: 'var(--color-pure-white)',
            padding: isMobile ? '1rem' : '1rem 2rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            borderBottom: `1px solid var(--color-gray-700)`,
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <Link to="/auth" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', transition: 'opacity var(--default-transition-duration) var(--ease-in-out)' }} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}>
              <TinxliLogo width={isMobile ? 100 : 120} height={isMobile ? 32 : 40} />
            </Link>
          </nav>
        )}

        {/* Impersonation Banner */}
        {impersonation.active && (
          <div style={{
            background: '#fef3c7',
            borderBottom: '2px solid #f59e0b',
            padding: '0.5rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            position: 'sticky',
            top: isMobile ? '57px' : 0,
            zIndex: 996,
          }}>
            <span style={{ fontSize: '0.85rem', color: '#92400e', fontWeight: 600 }}>
              Acting as {impersonation.vendorName || 'vendor'} — changes are attributed to your super-admin account.
            </span>
            <button
              onClick={stopImpersonation}
              style={{
                fontSize: '0.8rem', padding: '0.3rem 0.75rem', borderRadius: '4px',
                border: '1px solid #d97706', background: '#fffbeb', color: '#92400e',
                cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
              }}
            >
              Exit
            </button>
          </div>
        )}

        {/* Main Content */}
        <main style={{
          flex: 1,
          padding: isMobile ? '1rem' : '2rem',
          background: 'var(--color-gray-50)',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <Outlet />
        </main>

        {/* Footer */}
        <footer style={{
          background: 'var(--color-pure-white)',
          color: 'var(--color-tixxly-navy)',
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          textAlign: 'center',
          borderTop: `1px solid var(--color-gray-200)`,
          boxShadow: '0 -1px 3px 0 rgba(0, 0, 0, 0.05)'
        }}>
          <p style={{ margin: 0, fontSize: 'var(--text-sm)' }}>&copy; 2024 Tixxly. All rights reserved.</p>
        </footer>
      </div>
    </div>
  )
}

